import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getCollectionBySlug, getCollectionPieces, COLLECTIONS } from "../data/site";

function StatusPill({ status }) {
  if (status === "available")
    return (
      <span className="inline-block bg-[hsl(var(--accent))] text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
        Available
      </span>
    );
  if (status === "sold")
    return (
      <span className="inline-block bg-[hsl(var(--ink-soft))] text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
        Sold
      </span>
    );
  if (status === "on-loan")
    return (
      <span className="inline-block bg-foreground text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
        On Loan · Reserve
      </span>
    );
  return (
    <span className="inline-block bg-foreground text-background px-2 py-0.5 text-[0.625rem] uppercase tracking-[0.18em]">
      Private
    </span>
  );
}

function PieceCard({ p }) {
  const { piece, meta, seriesSlug, seriesLabel } = p;
  return (
    <Link
      to={`/work/${seriesSlug}/${piece.id}`}
      className="art-card block group"
      data-testid={`collection-piece-${piece.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
        <img
          src={piece.image}
          alt={piece.title}
          loading="lazy"
          className="art-img w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3">
          <StatusPill status={meta.status} />
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="serif italic text-xl md:text-2xl">{piece.title}</h3>
          <span className="label">{piece.year}</span>
        </div>
        <p className="label mt-1">
          {seriesLabel} · {piece.size}
        </p>
        {(meta.status === "available" || meta.status === "on-loan") && meta.price && (
          <p className="serif text-lg mt-2">${meta.price.toLocaleString()}</p>
        )}
      </div>
    </Link>
  );
}

export default function CollectionDetail() {
  const { slug } = useParams();
  const collection = getCollectionBySlug(slug);

  if (!collection) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <Link to="/collections" className="label link-underline">
          ← All Collections
        </Link>
        <h1 className="serif text-5xl mt-6">Collection not found.</h1>
      </div>
    );
  }

  const pieces = getCollectionPieces(collection);

  return (
    <div data-testid={`collection-${collection.slug}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10 md:pt-16">
        <Link
          to="/collections"
          className="inline-flex items-center gap-2 label link-underline"
          data-testid="collection-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All Collections
        </Link>
      </div>

      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-12 md:pb-16">
        <p className="label mb-4">{collection.eyebrow}</p>
        <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
          {collection.label}
        </h1>
        <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">
          {collection.blurb}
        </p>
        <p className="mt-6 label">
          {pieces.length} {pieces.length === 1 ? "piece" : "pieces"}
        </p>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        {pieces.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
            {pieces.map((p) => (
              <PieceCard key={p.piece.id} p={p} />
            ))}
          </div>
        ) : (
          <p className="text-[hsl(var(--ink-soft))]">
            Nothing in this collection just yet — check back soon.
          </p>
        )}
      </section>

      {/* Other collections */}
      <section className="border-t border-border bg-[hsl(var(--surface))]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="label mb-3">Keep exploring</p>
          <h2 className="serif text-3xl md:text-4xl tracking-tight mb-10">
            Other collections
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            {COLLECTIONS.filter((c) => c.slug !== collection.slug).map((c) => (
              <Link
                key={c.slug}
                to={`/collections/${c.slug}`}
                className="block border-t border-border pt-6 group"
                data-testid={`other-collection-${c.slug}`}
              >
                <p className="label">{c.eyebrow}</p>
                <h3 className="serif text-2xl md:text-3xl mt-2 group-hover:italic transition-all">
                  {c.label}
                </h3>
                <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
                  {c.blurb}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
